import React, { forwardRef } from 'react'
import styled, { css } from 'styled-components'
import { lineClamp } from 'sutra/components/Text/text-styles'
import { brand } from 'sutra/tokens/colors'
import { typography } from 'sutra/tokens/typography'

export type HeadingLevel = 'xl' | '1' | '2' | '3' | '4' | '5' | '6'

export interface HeadingProps extends React.HTMLAttributes<HTMLElement> {
  $level?: HeadingLevel
  // 1 truncates to a single line.
  $lineClamp?: number
  // `h2` by default.
  as?: React.ElementType
}

const levelStyles: Record<HeadingLevel, ReturnType<typeof css>> = {
  xl: css`
    font-size: 60px;
    line-height: 72px;
  `,
  '1': css`
    font-size: 48px;
    line-height: 60px;
  `,
  '2': css`
    font-size: 36px;
    line-height: 44px;
  `,
  '3': css`
    font-size: 30px;
    line-height: 38px;
  `,
  '4': css`
    font-size: 24px;
    line-height: 32px;
    letter-spacing: -0.01em;
  `,
  '5': css`
    font-size: ${typography.text.xl.fontSize}px;
    line-height: ${typography.text.xl.lineHeight}px;
    letter-spacing: -0.01em;
  `,
  '6': css`
    font-size: ${typography.text.md.fontSize}px;
    line-height: ${typography.text.md.lineHeight}px;
    letter-spacing: -0.01em;
  `,
}

const StyledHeading = styled.h2<{ $level: HeadingLevel; $lineClamp?: number }>`
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${brand.stillNight[800]};
  margin: 0;
  text-wrap: balance;
  ${({ $level }) => levelStyles[$level]}
  ${({ $lineClamp }) => $lineClamp && lineClamp($lineClamp)}
`

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  function Heading({ $level = '2', $lineClamp, as, ...rest }, ref) {
    return (
      <StyledHeading
        {...rest}
        ref={ref}
        as={as}
        $level={$level}
        $lineClamp={$lineClamp}
      />
    )
  },
)

Heading.displayName = 'Heading'
